export type UserRole = 'admin' | 'team' | 'client';
export type MemberRole = 'manager' | 'commenter' | 'viewer';
export type ItemType = 'website' | 'image' | 'video';
export type ReviewStatus = 'in_review' | 'changes_requested' | 'approved';
export type CommentStatus = 'open' | 'in_progress' | 'resolved';
export type Viewport = 'desktop' | 'tablet' | 'mobile';

export interface User {
  id: string;
  email: string;
  username: string | null;
  name: string;
  company: string | null;
  role: UserRole;
  isActive: boolean;
  hasPassword: boolean;
  createdAt: string;
  lastLoginAt: string | null;
}

export interface Session {
  user: User | null;
  scopeProjectId?: string | null;
  isStaff?: boolean;
  isAdmin?: boolean;
}

export interface Meta {
  appUrl: string;
  proxyUrl: string;
  emailEnabled: boolean;
  storage: string;
  maxUploadMb: number;
  maxAttachmentMb: number;
  uploadChunkBytes: number;
}

export interface Project {
  id: string;
  slug: string;
  name: string;
  clientName: string | null;
  description: string | null;
  status: 'active' | 'archived';
  createdAt: string;
  updatedAt: string;
  counts: { items: number; files: number; members: number; openComments: number; resolvedComments: number };
  myRole: string | null;
  permissions?: { canComment: boolean; canManage: boolean; canVersion: boolean };
  shareUrl: string;
  gate?: { enabled: boolean; username: string | null; hasPassword: boolean };
}

export interface Asset {
  id: string;
  kind: 'file' | 'version' | 'attachment';
  filename: string;
  mime: string;
  media: 'image' | 'video' | 'other';
  size: number;
  width: number | null;
  height: number | null;
  duration: number | null;
  note: string | null;
  uploadedById: string | null;
  uploadedByName: string | null;
  createdAt: string;
  url: string;
  downloadUrl: string;
}

export interface ItemVersion {
  id: string;
  versionNo: number;
  note: string | null;
  createdByName: string | null;
  createdAt: string;
  asset: Asset;
}

export interface Item {
  id: string;
  projectId: string;
  type: ItemType;
  title: string;
  description: string | null;
  url: string | null;
  position: number;
  reviewStatus: ReviewStatus;
  reviewByName: string | null;
  reviewAt: string | null;
  currentVersion: ItemVersion | null;
  versions?: ItemVersion[];
  counts: { open: number; resolved: number };
  createdAt: string;
  updatedAt: string;
}

export interface ElementInfo {
  tag: string | null;
  id: string | null;
  classes: string[];
  text: string | null;
  html: string | null;
  rect: { x: number; y: number; width: number; height: number } | null;
  attributes: Record<string, string>;
}

export interface DomAnchor {
  type: 'dom';
  selector: string;
  xPercent: number;
  yPercent: number;
  pageX: number | null;
  pageY: number | null;
  documentWidth?: number | null;
  documentHeight?: number | null;
  viewportWidth?: number | null;
  viewportHeight?: number | null;
  scrollX?: number | null;
  scrollY?: number | null;
  pageUrl: string | null;
  pagePath: string | null;
  viewport: Viewport | null;
  element: ElementInfo;
}

export interface PointAnchor {
  type: 'point';
  x: number;
  y: number;
  mediaWidth?: number | null;
  mediaHeight?: number | null;
}

export interface RegionAnchor {
  type: 'region';
  x: number;
  y: number;
  width: number;
  height: number;
  mediaWidth?: number | null;
  mediaHeight?: number | null;
}

export interface TimeAnchor {
  type: 'time';
  time: number;
  timecode?: string;
  duration?: number | null;
  x?: number | null;
  y?: number | null;
  width?: number | null;
  height?: number | null;
  mediaWidth?: number | null;
  mediaHeight?: number | null;
}

export type Anchor = { type: 'none' } | DomAnchor | PointAnchor | RegionAnchor | TimeAnchor;

export interface Author {
  id: string | null;
  name: string;
  email: string | null;
  kind: 'user' | 'api';
}

export interface Reply {
  id: string;
  author: Author;
  body: string;
  attachments: Asset[];
  source: string;
  createdAt: string;
  editedAt: string | null;
}

export interface Comment {
  id: string;
  number: number;
  projectId: string;
  itemId: string | null;
  versionId: string | null;
  author: Author;
  body: string;
  category: string | null;
  priority: string | null;
  status: CommentStatus;
  statusByName: string | null;
  statusAt: string | null;
  anchor: Anchor;
  attachments: Asset[];
  replies: Reply[];
  unread: boolean;
  source: string;
  createdAt: string;
  updatedAt: string;
  editedAt: string | null;
}

export interface Member extends User {
  memberRole: MemberRole;
  addedVia: string;
  addedAt: string;
}

export interface Person {
  id: string;
  name: string;
  role: UserRole;
}

export interface ActivityEntry {
  id: number;
  action: string;
  actorName: string | null;
  summary: string | null;
  ip: string | null;
  createdAt: string;
}

export interface ApiKey {
  id: string;
  name: string;
  prefix: string;
  scope: 'read' | 'write';
  projectId: string | null;
  projectName: string | null;
  createdAt: string;
  lastUsedAt: string | null;
}

export interface ErrorEntry {
  id: number;
  source: string;
  scope: string | null;
  message: string;
  stack: string | null;
  context: Record<string, unknown> | null;
  count: number;
  firstAt: string;
  lastAt: string;
}

export interface ViewInfo {
  src: string;
  label: string;
  proxyOrigin: string;
  realOrigin: string;
}

export const CATEGORIES = ['design', 'copy', 'navigation', 'structure', 'content', 'bug', 'seo', 'other'] as const;

export const CATEGORY_LABELS: Record<string, string> = {
  design: 'Design',
  copy: 'Copy',
  navigation: 'Navigation',
  structure: 'Sitemap / structure',
  content: 'Content',
  bug: 'Bug',
  seo: 'SEO',
  other: 'Other',
};

export const VIEWPORT_WIDTHS: Record<Viewport, number> = { desktop: 1440, tablet: 768, mobile: 390 };
